import { cssColour, dispose, mountScene, observeScene, openRenderer } from "./renderer.js";
import type { ResourceScope } from "./lifecycle.js";
/**
 * La vue animée de la chute (Three.js).
 *
 * C'est un **simulateur**, pas un schéma, et le traitement s'en ressent : deux
 * corps éclairés tombent en temps réel sur un sol, au lieu du fil de fer
 * volontairement schématique d'Ératosthène. La différence n'est pas
 * décorative. Le schéma illustre une géométrie qu'on ne peut pas photographier ;
 * ceci montre une scène qu'on peut reproduire avec un escabeau, et il vaut
 * mieux qu'elle en ait l'air.
 *
 * ## Le temps est une entrée, jamais une horloge
 *
 * L'animation ne calcule rien. Elle avance un compteur de secondes et demande
 * la scène **à cet instant-là** ; le noyau répond par une fonction pure
 * (`fallHeightAt`). Deux conséquences, et ce sont les deux qui comptent :
 *
 * - la trajectoire affichée ne dépend ni du rythme de la machine, ni du nombre
 *   d'images rendues. Une machine lente montre la même chute, moins fluide.
 * - reculer le curseur de temps rejoue exactement les mêmes positions. Rien
 *   n'est accumulé d'une image à l'autre, donc rien ne dérive.
 *
 * L'horloge du navigateur ne sert qu'à décider **quand regarder** — ce qui est
 * un choix d'affichage, pas un calcul (CLAUDE.md §5).
 *
 * ## Ce que la vue ne fait pas
 *
 * Elle ne départage pas. Voir un corps toucher le sol avant l'autre montre un
 * désaccord entre deux modèles, pas lequel décrit le monde : il faudrait pour
 * cela lâcher deux vrais objets et les chronométrer. La mise en garde le dit,
 * et la section de confrontation refuse toujours faute de relevés.
 */

import type { FallScene, FallSceneRequest, Vec3 } from "./fall-scene.js";
import type { SceneHandle } from "../experiment/types.js";
import { buildFallScene, fallBoundsCentre, fallScenePoints, groundGrid } from "./fall-scene.js";
import { bindPlayer, renderPlayerControls } from "./player.js";
import { escape, escapeAttribute } from "./format.js";
import { fr } from "../i18n/fr.js";

type ThreeModule = typeof import("three");

/** Direction du regard : de face, légèrement au-dessus — on regarde tomber. */
const VIEW_DIRECTION = { x: 0.25, y: 0.18, z: 1 } as const;
const FIELD_OF_VIEW = 40;
const FRAMING_MARGIN = 1.12;
const TRACK_RADIUS = 0.004;

export interface FallPlayback {
  /** Durée à animer, en secondes : la plus longue des chutes en présence. */
  readonly duration: () => number;
  /** La scène à l'instant demandé. Fonction pure de cet instant. */
  readonly at: (elapsed: number) => FallSceneRequest;
}

// ── Coquille HTML ────────────────────────────────────────────────────────────

/**
 * Les commandes sont des éléments natifs — bouton et `input[type=range]` —,
 * donc atteignables au clavier sans une ligne de JavaScript (CLAUDE.md §3).
 */
export function renderFallShell(
  bodies: readonly { readonly id: string; readonly label: string }[],
): string {
  // La place dans la scène est le signal non chromatique : la légende nomme la
  // position avant la couleur, et reste vraie sur une capture en noir et blanc.
  const legend = bodies
    .map((body, rank) =>
      [
        `<li data-nature="predicted" data-model="${escapeAttribute(body.id)}"`,
        ` data-series="${rank}">${escape(fr.fallSceneSide(rank, bodies.length))} — `,
        `${escape(body.label)}</li>`,
      ].join(""),
    )
    .join("");

  return [
    `<figure class="verlet-scene">`,
    renderPlayerControls("fall"),
    `<div class="verlet-scene__stage verlet-scene__stage--tall" data-fall-stage role="img"`,
    ` aria-label="${escapeAttribute(fr.fallSceneAlt)}">`,
    `<p class="verlet-scene__fallback">${escape(fr.schematicLoading)}</p>`,
    `</div>`,
    `<ul class="verlet-legend">${legend}</ul>`,
    `<figcaption class="verlet-caveat">${escape(fr.fallSceneCaveat)}</figcaption>`,
    `</figure>`,
  ].join("");
}

// ── Montage ──────────────────────────────────────────────────────────────────

/**
 * Attache la vue et rend de quoi la redessiner puis la retirer.
 *
 * Ne rejette jamais : sans WebGL la page reste vraie, elle perd une
 * illustration. Le graphe et la table disent déjà tout ce que cette vue montre.
 */
export function mountFallScene(root: ParentNode, playback: FallPlayback): SceneHandle {
  return mountScene(root, "[data-fall-stage]", fr.schematicUnavailable, (scope) => attach(root, playback, scope));
}

async function attach(
  root: ParentNode,
  playback: FallPlayback,
  scope: ResourceScope,
): Promise<{ readonly redraw: () => void } | undefined> {
  const stage = root.querySelector<HTMLElement>("[data-fall-stage]");
  if (stage === null) return;

  const opened = await openRenderer(stage, scope);
  if (!opened) return;
  const { three, renderer } = opened;

  const scene = new three.Scene();
  scope.own(() => dispose(scene));
  const camera = new three.PerspectiveCamera(FIELD_OF_VIEW, 1, 0.01, 100);
  scene.add(camera);

  // Deux lumières, contrairement au schéma qui n'en a aucune : ici les corps
  // sont des objets, pas des traits, et une sphère sans ombré est un disque.
  const ambient = new three.AmbientLight(0xffffff, 1.6);
  const key = new three.DirectionalLight(0xffffff, 1.5);
  key.position.set(0.6, 1, 0.8);
  scene.add(ambient);
  scene.add(key);

  let content = new three.Group();
  scene.add(content);

  // Les billes sont les seules choses qui bougent. Reconstruire le sol, son
  // quadrillage et les rails soixante fois par seconde marchait, mais allouait
  // une dizaine de géométries par image pour les jeter aussitôt : c'est
  // exactement ce qui fait saccader une animation sur une machine modeste. On
  // ne reconstruit donc que lorsque la scène change de nature — un modèle qui
  // cesse de prédire, par exemple —, et on se contente sinon de déplacer.
  let spheres: InstanceType<ThreeModule["Mesh"]>[] = [];
  let builtFor = "";
  let framedAspect = 0;

  const signatureOf = (built: FallScene): string =>
    built.bodies.map((body) => body.id).join("|");

  // ── L'état de lecture ──────────────────────────────────────────────────
  // La boucle, les boutons et le curseur de temps vivent dans `player.ts` :
  // l'orbite en réclamait les mêmes, et deux copies d'une boucle
  // `requestAnimationFrame` finissent toujours par diverger.
  let lastElapsed = 0;

  const draw = (): void => {
    const built = buildFallScene(playback.at(lastElapsed));
    const signature = signatureOf(built);

    if (signature !== builtFor) {
      scene.remove(content);
      dispose(content);
      const rebuilt = build(three, built);
      content = rebuilt.group;
      spheres = rebuilt.spheres;
      builtFor = signature;
      scene.add(content);
    } else {
      built.bodies.forEach((body, index) => {
        spheres[index]?.position.set(body.centre.x, body.centre.y, body.centre.z);
      });
    }

    const width = stage.clientWidth;
    const height = stage.clientHeight;
    if (width === 0 || height === 0) return;
    renderer.setSize(width, height, false);
    camera.aspect = width / height;

    // La caméra ne suit pas les billes : le cadrage est celui de la scène —
    // le sol et le point de lâcher —, qui ne bouge pas d'une image à l'autre.
    // Le recalculer à chaque frame ferait respirer le cadre au rythme de la
    // chute, ce qui donnerait l'illusion d'un zoom que personne n'a demandé.
    if (camera.aspect !== framedAspect) {
      frame(three, camera, built, camera.aspect);
      framedAspect = camera.aspect;
    }
    renderer.render(scene, camera);
  };

  const player = bindPlayer(root, playback.duration, (elapsed) => {
    lastElapsed = elapsed;
    draw();
  });
  scope.own(player.detach);

  const onThemeChange = (): void => {
    builtFor = "";
    draw();
  };
  observeScene(scope, stage, draw, onThemeChange);

  scope.own(() => {
    scene.remove(content);
    dispose(content);
  });

  return { redraw: player.redraw };
}

// ── Construction de la scène ─────────────────────────────────────────────────

function vector(three: ThreeModule, v: Vec3): InstanceType<ThreeModule["Vector3"]> {
  return new three.Vector3(v.x, v.y, v.z);
}

function segment(
  three: ThreeModule,
  from: Vec3,
  to: Vec3,
  radius: number,
  material: InstanceType<ThreeModule["MeshBasicMaterial"]>,
): InstanceType<ThreeModule["Mesh"]> | undefined {
  const start = vector(three, from);
  const axis = vector(three, to).sub(start);
  const length = axis.length();
  if (length < 1e-9) return undefined;

  const mesh = new three.Mesh(new three.CylinderGeometry(radius, radius, length, 8), material);
  mesh.position.copy(start).add(axis.clone().multiplyScalar(0.5));
  mesh.quaternion.setFromUnitVectors(new three.Vector3(0, 1, 0), axis.clone().normalize());
  return mesh;
}

/** Les couleurs de rang, les mêmes que celles des courbes du graphe. */
const SERIES_VARIABLES = ["--series-a", "--series-b", "--series-c"] as const;
const SERIES_FALLBACKS = ["#2c5f5a", "#8a4b12", "#4a4668"] as const;

function seriesColour(three: ThreeModule, rank: number): InstanceType<ThreeModule["Color"]> {
  const index = rank % SERIES_VARIABLES.length;
  return new three.Color(
    cssColour(SERIES_VARIABLES[index] ?? "--ink", SERIES_FALLBACKS[index] ?? "#1b1a17"),
  );
}

interface BuiltScene {
  readonly group: InstanceType<ThreeModule["Group"]>;
  /** Les billes, dans l'ordre des corps : la boucle n'a qu'elles à déplacer. */
  readonly spheres: InstanceType<ThreeModule["Mesh"]>[];
}

function build(three: ThreeModule, scene: FallScene): BuiltScene {
  const group = new three.Group();
  const spheres: InstanceType<ThreeModule["Mesh"]>[] = [];

  const paper = new three.Color(cssColour("--paper", "#faf8f4"));
  const rule = new three.Color(cssColour("--rule", "#d8d2c6"));
  const inkSoft = new three.Color(cssColour("--ink-soft", "#5c584f"));

  // ── Le sol ─────────────────────────────────────────────────────────────
  const disc = new three.Mesh(
    new three.CircleGeometry(scene.groundRadius, 64),
    new three.MeshBasicMaterial({ color: paper }),
  );
  disc.rotation.x = -Math.PI / 2;
  disc.position.y = -0.001;
  group.add(disc);

  const gridMaterial = new three.LineBasicMaterial({ color: rule });
  const gridPoints: InstanceType<ThreeModule["Vector3"]>[] = [];
  for (const [from, to] of groundGrid(scene.groundRadius)) {
    gridPoints.push(vector(three, from), vector(three, to));
  }
  group.add(
    new three.LineSegments(
      new three.BufferGeometry().setFromPoints(gridPoints),
      gridMaterial,
    ),
  );

  // ── Les corps, et le rail qu'ils descendent ────────────────────────────
  const trackMaterial = new three.MeshBasicMaterial({ color: rule });
  const markMaterial = new three.MeshBasicMaterial({ color: inkSoft });

  scene.bodies.forEach((body, rank) => {
    const [top, bottom] = body.track;
    const track = segment(three, top, bottom, TRACK_RADIUS, trackMaterial);
    if (track !== undefined) group.add(track);

    // Une marque au sol sous chaque corps : elle donne la profondeur, et elle
    // dit où il va tomber avant qu'il n'y soit.
    const mark = new three.Mesh(
      new three.RingGeometry(body.radius * 0.9, body.radius * 1.15, 24),
      markMaterial,
    );
    mark.rotation.x = -Math.PI / 2;
    mark.position.set(bottom.x, 0.002, bottom.z);
    group.add(mark);

    const sphere = new three.Mesh(
      new three.SphereGeometry(body.radius, 32, 24),
      new three.MeshLambertMaterial({ color: seriesColour(three, rank) }),
    );
    sphere.position.copy(vector(three, body.centre));
    group.add(sphere);
    spheres.push(sphere);
  });

  return { group, spheres };
}

// ── Cadrage ──────────────────────────────────────────────────────────────────

/**
 * Cadre la scène entière depuis une direction fixe.
 *
 * Le recul est calculé sur les points **réellement occupés**, pas sur une
 * sphère englobante. La première version faisait l'inverse et la scène tenait
 * dans un cinquième du cadre : le sol est un disque, les coins de sa boîte ne
 * contiennent rien, et la caméra reculait pour les cadrer. C'est le même défaut
 * que le schéma d'Ératosthène avait eu, pour la même raison.
 */
function frame(
  three: ThreeModule,
  camera: InstanceType<ThreeModule["PerspectiveCamera"]>,
  scene: FallScene,
  aspect: number,
): void {
  const centre = vector(three, fallBoundsCentre(scene));

  const direction = new three.Vector3(
    VIEW_DIRECTION.x,
    VIEW_DIRECTION.y,
    VIEW_DIRECTION.z,
  ).normalize();
  const forward = direction.clone().negate();
  const right = new three.Vector3()
    .crossVectors(forward, new three.Vector3(0, 1, 0))
    .normalize();
  const up = new three.Vector3().crossVectors(right, forward);

  const halfVertical = ((FIELD_OF_VIEW / 2) * Math.PI) / 180;
  const halfHorizontal = Math.atan(Math.tan(halfVertical) * aspect);
  const tanHorizontal = Math.tan(halfHorizontal);
  const tanVertical = Math.tan(halfVertical);

  let distance = 0;
  let depth = 0;
  for (const point of fallScenePoints(scene)) {
    const offset = vector(three, point).sub(centre);
    const along = offset.dot(direction);
    distance = Math.max(
      distance,
      along + (Math.abs(offset.dot(right)) * FRAMING_MARGIN) / tanHorizontal,
      along + (Math.abs(offset.dot(up)) * FRAMING_MARGIN) / tanVertical,
    );
    depth = Math.max(depth, Math.abs(along));
  }

  camera.position.copy(centre).add(direction.multiplyScalar(distance));
  camera.lookAt(centre);
  camera.near = Math.max(0.01, distance - depth * 1.5);
  camera.far = distance + depth * 1.5;
  camera.updateProjectionMatrix();
}

// ── Nettoyage ────────────────────────────────────────────────────────────────
